import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Bot } from 'lucide-react';
import AdminDashboardHome from './AdminDashboardHome';
import AcceleratorsCRUD from './AcceleratorsCRUD';
import CategoriesCRUD from './CategoriesCRUD';
import SubcategoriesCRUD from './SubcategoriesCRUD';
import ConfigurationCRUD from './ConfigurationCRUD';
import AnalyticsCRUD from './AnalyticsCRUD';
import GeneralConfigCRUD from './GeneralConfigCRUD';
// Import other CRUD components from root level
import TechStackCRUD from '../../TechStackCRUD';
import AdminUsersCRUD from '../../AdminUsersCRUD';
import RecommendationsCRUD from '../../RecommendationsCRUD';
// Import existing config components
import HeroSectionCRUD from '../../HeroSectionCRUD';
import EmailSettingsCRUD from '../../EmailSettingsCRUD';
import CustomersCRUD from '../../CustomersCRUD';
import CustomModelsCRUD from '../../CustomModelsCRUD';
import ValuePropositionsCRUD from '../../ValuePropositionsCRUD';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const AdminDashboard = ({ onLogout }) => {
  const [currentView, setCurrentView] = useState('dashboard');
  const [agents, setAgents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [config, setConfig] = useState({});
  const [loading, setLoading] = useState(false);
  const [expandedMenu, setExpandedMenu] = useState(null);

  const menuItems = [
    { id: 'dashboard', name: 'Dashboard', icon: '🏠' },
    { id: 'agents', name: 'Accelerators', icon: '🚀' },
    { id: 'categories', name: 'Categories', icon: '📁' },
    { id: 'subcategories', name: 'Subcategories', icon: '📂' },
    { id: 'personas', name: 'Tech Stacks', icon: '🔧' },
    { 
      id: 'config', 
      name: 'Site Configuration', 
      icon: '⚙️',
      subItems: [
        { id: 'config-hero', name: 'Hero Section', icon: '🎯' },
        { id: 'config-value-props', name: 'Value Propositions', icon: '⭐' },
        { id: 'config-tech-stack', name: 'Tech Stack', icon: '🔧' },
        { id: 'config-custom-models', name: 'Custom Accelerators', icon: '🎨' },
        { id: 'config-customers', name: 'Customers', icon: '👥' },
        { id: 'config-email', name: 'Email Settings', icon: '📧' },
        { id: 'config-analytics', name: 'Analytics', icon: '📊' },
        { id: 'config-general', name: 'General Settings', icon: '⚙️' }
      ]
    },
    { id: 'admin-users', name: 'Admin Users', icon: '👤' },
    { id: 'recommendations', name: 'Recommendations', icon: '💡' },
  ];

  useEffect(() => {
    fetchAllData();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [agentsRes, categoriesRes, subcategoriesRes, configRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/admin/accelerators`, { headers: getAuthHeaders() }),
        axios.get(`${API_BASE_URL}/api/categories`),
        axios.get(`${API_BASE_URL}/api/subcategories`),
        axios.get(`${API_BASE_URL}/api/config`)
      ]);

      setAgents(agentsRes.data);
      setCategories(categoriesRes.data);
      setSubcategories(subcategoriesRes.data);
      setConfig(configRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
    setLoading(false);
  };

  const renderContent = () => {
    switch (currentView) {
      case 'dashboard':
        return <AdminDashboardHome agents={agents} categories={categories} subcategories={subcategories} onViewChange={setCurrentView} />;
      case 'agents':
        return <AcceleratorsCRUD agents={agents} categories={categories} subcategories={subcategories} onUpdate={fetchAllData} />;
      case 'categories':
        return <CategoriesCRUD categories={categories} onUpdate={fetchAllData} />;
      case 'personas':
        return <TechStackCRUD />;
      case 'subcategories':
        return <SubcategoriesCRUD subcategories={subcategories} categories={categories} onUpdate={fetchAllData} />;
      case 'config':
        // Configuration overview or redirect to first sub-item
        return (
          <div className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <div className="flex items-center">
                <div className="text-2xl mr-3">⚙️</div>
                <div>
                  <h4 className="text-lg font-semibold text-blue-900">Site Configuration</h4>
                  <p className="text-blue-700 mt-1">
                    Select a configuration section from the sidebar to get started.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <button 
                onClick={() => setCurrentView('config-hero')}
                className="bg-white rounded-lg shadow-sm p-6 text-left hover:shadow-md transition-shadow border border-gray-200"
              >
                <div className="text-2xl mb-3">🎯</div>
                <h5 className="font-semibold text-gray-900 mb-2">Hero Section</h5>
                <p className="text-sm text-gray-600">Configure the main landing page hero content and calls-to-action.</p>
              </button>
              
              <button 
                onClick={() => setCurrentView('config-value-props')}
                className="bg-white rounded-lg shadow-sm p-6 text-left hover:shadow-md transition-shadow border border-gray-200"
              >
                <div className="text-2xl mb-3">⭐</div>
                <h5 className="font-semibold text-gray-900 mb-2">Value Propositions</h5>
                <p className="text-sm text-gray-600">Manage value proposition cards and features showcase.</p>
              </button>
              
              <button 
                onClick={() => setCurrentView('config-analytics')}
                className="bg-white rounded-lg shadow-sm p-6 text-left hover:shadow-md transition-shadow border border-gray-200"
              >
                <div className="text-2xl mb-3">📊</div>
                <h5 className="font-semibold text-gray-900 mb-2">Analytics</h5>
                <p className="text-sm text-gray-600">Configure tracking codes and analytics settings.</p>
              </button>
              
              <button 
                onClick={() => setCurrentView('config-email')}
                className="bg-white rounded-lg shadow-sm p-6 text-left hover:shadow-md transition-shadow border border-gray-200"
              >
                <div className="text-2xl mb-3">📧</div>
                <h5 className="font-semibold text-gray-900 mb-2">Email Settings</h5>
                <p className="text-sm text-gray-600">Setup SMTP configuration and email templates.</p>
              </button>
              
              <button 
                onClick={() => setCurrentView('config-tech-stack')}
                className="bg-white rounded-lg shadow-sm p-6 text-left hover:shadow-md transition-shadow border border-gray-200"
              >
                <div className="text-2xl mb-3">🔧</div>
                <h5 className="font-semibold text-gray-900 mb-2">Tech Stack</h5>
                <p className="text-sm text-gray-600">Manage technology stack showcase and integrations.</p>
              </button>
              
              <button 
                onClick={() => setCurrentView('config-general')}
                className="bg-white rounded-lg shadow-sm p-6 text-left hover:shadow-md transition-shadow border border-gray-200"
              >
                <div className="text-2xl mb-3">⚙️</div>
                <h5 className="font-semibold text-gray-900 mb-2">General Settings</h5>
                <p className="text-sm text-gray-600">Configure other miscellaneous website settings.</p>
              </button>
            </div>
          </div>
        );
      case 'config-hero':
        return <HeroSectionCRUD config={config} onUpdate={fetchAllData} />;
      case 'config-value-props':
        return <ValuePropositionsCRUD config={config} onUpdate={fetchAllData} />;
      case 'config-tech-stack':
        return <TechStackCRUD config={config} onUpdate={fetchAllData} />;
      case 'config-custom-models':
        return <CustomModelsCRUD config={config} onUpdate={fetchAllData} />;
      case 'config-customers':
        return <CustomersCRUD config={config} onUpdate={fetchAllData} />;
      case 'config-email':
        return <EmailSettingsCRUD config={config} onUpdate={fetchAllData} />;
      case 'config-analytics':
        return <AnalyticsCRUD config={config} onUpdate={fetchAllData} />;
      case 'config-general':
        return <GeneralConfigCRUD config={config} onUpdate={fetchAllData} />;
      case 'admin-users':
        return <AdminUsersCRUD />;
      case 'recommendations':
        return <RecommendationsCRUD />;
      default:
        return <AdminDashboardHome agents={agents} categories={categories} subcategories={subcategories} onViewChange={setCurrentView} />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <div className="w-64 bg-white shadow-lg flex flex-col h-screen">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex flex-col space-y-1">
            <div className="flex items-center space-x-2">
              <Bot className="h-8 w-8 text-primary-600" />
              <span className="text-xl font-bold text-gray-900">Admin Panel</span>
            </div>
            <div className="text-sm text-gray-600 font-medium">
              KL Accelerators
            </div>
          </div>
        </div>
        
        {/* Scrollable Navigation */}
        <div className="flex-1 overflow-y-auto">
          <nav className="mt-6 px-3 pb-6">
          {menuItems.map((item) => (
            <div key={item.id}>
              <button
                onClick={() => {
                  if (item.subItems) {
                    // Toggle sub-menu expansion
                    if (expandedMenu === item.id) {
                      setExpandedMenu(null); // Close if already open
                    } else {
                      setExpandedMenu(item.id); // Open sub-menu
                      setCurrentView(item.id); // Also navigate to the main section
                    }
                  } else {
                    setCurrentView(item.id);
                    setExpandedMenu(null); // Close any open sub-menus
                  }
                }}
                className={`w-full flex items-center space-x-3 px-4 py-3 text-left rounded-lg transition-colors mb-1 ${
                  currentView === item.id || (item.subItems && item.subItems.some(sub => sub.id === currentView))
                    ? 'bg-primary-100 text-primary-700 border-r-2 border-primary-600'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <span className="text-lg">{item.icon}</span>
                <span className="font-medium">{item.name}</span>
                {item.subItems && (
                  <span className="ml-auto text-xs">
                    {expandedMenu === item.id ? '▼' : '▶'}
                  </span>
                )}
              </button>
              
              {/* Sub-menu items */}
              {item.subItems && expandedMenu === item.id && (
                <div className="ml-4 mt-1 space-y-1">
                  {item.subItems.map((subItem) => (
                    <button
                      key={subItem.id}
                      onClick={() => setCurrentView(subItem.id)}
                      className={`w-full flex items-center space-x-3 px-4 py-2 text-left rounded-lg transition-colors text-sm ${
                        currentView === subItem.id
                          ? 'bg-primary-50 text-primary-600 border-l-2 border-primary-600'
                          : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <span className="text-sm">{subItem.icon}</span>
                      <span>{subItem.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>
        </div>
        
        {/* Fixed Logout Section */}
        <div className="flex-shrink-0 p-4 border-t border-gray-200 bg-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center">
                <span className="text-white text-sm font-semibold">SA</span>
              </div>
              <span className="text-sm font-medium text-gray-700">Super Admin</span>
            </div>
            <button
              onClick={onLogout}
              className="text-gray-400 hover:text-red-600 transition-colors"
              title="Logout"
            >
              <span className="text-lg">🚪</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        <div className="p-8">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Loading...</p>
              </div>
            </div>
          ) : (
            renderContent()
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;